/**
 * Country flags beside a place. The flags are SVGs from flag-icons (MIT) in
 * static/flags/, named by ISO 3166 code; emoji flags are not used because
 * Windows shows them as two letters.
 */

const COUNTRIES: Record<string, string> = {
  Australia: "au",
  Austria: "at",
  Brazil: "br",
  Canada: "ca",
  China: "cn",
  Colombia: "co",
  Estonia: "ee",
  France: "fr",
  Germany: "de",
  Greece: "gr",
  Iran: "ir",
  Italy: "it",
  Japan: "jp",
  Liechtenstein: "li",
  Malaysia: "my",
  Mexico: "mx",
  Poland: "pl",
  Portugal: "pt",
  "Puerto Rico": "pr",
  Russia: "ru",
  "Saudi Arabia": "sa",
  Singapore: "sg",
  "South Korea": "kr",
  Spain: "es",
  Switzerland: "ch",
  UAE: "ae",
  UK: "gb",
  USA: "us",
};

const PATTERN = new RegExp(
  `\\b(${Object.keys(COUNTRIES)
    .sort((a, b) => b.length - a.length)
    .join("|")})\\b`,
  "g",
);

/** The country a place names, taking the last one: "Abu Dhabi, UAE (Yas Marina)" is UAE. */
export function countryOf(place: string): { name: string; code: string } | null {
  const found = Array.from(place.matchAll(PATTERN)).pop();
  return found ? { name: found[1], code: COUNTRIES[found[1]] } : null;
}

/** A flag image for the place, or null when it names no country we have a flag for. */
export function flagFor(place: string): HTMLImageElement | null {
  const country = countryOf(place);
  if (!country) return null;
  const img = document.createElement("img");
  img.className = "flag";
  img.src = `/flags/${country.code}.svg`;
  // The country is already in the text beside it, so the image is decoration.
  img.alt = "";
  img.title = country.name;
  img.width = 20;
  img.height = 15;
  img.loading = "lazy";
  return img;
}

/** Puts a flag at the start of a cell, once. */
export function flagCell(cell: HTMLElement | undefined): void {
  if (!cell || cell.querySelector(".flag")) return;
  const flag = flagFor(cell.textContent ?? "");
  if (flag) cell.prepend(flag);
}

/** Flags the named column of every table under the selector. */
export function flagColumn(selector: string, column: string): void {
  for (const table of document.querySelectorAll<HTMLTableElement>(`${selector} table`)) {
    // Inline rather than calendar's columnIndex, which imports this module.
    const col = Array.from(table.querySelectorAll("thead th")).findIndex((th) => th.textContent?.trim().toLowerCase() === column);
    if (col < 0) continue;
    for (const row of Array.from(table.tBodies[0]?.rows ?? [])) flagCell(row.cells[col]);
  }
}
